import React, { useEffect, useState } from "react";
import { Flex } from "rebass";

import { Media } from "../../contexts/media/MediaContext";
import { FeatureToggle, IconPack, TabbedContentBlock, TabbedContentContainer } from "../../models/app-data-model";
import ContentAccordion from "../accordion/content-accordion";
import CallToAction from "../call-to-actions/call-to-action/call-to-action";
import DynamicCardContainer from "../dynamic-card-container/dynamic-card-container";
import NonClickableContentCardGrid from "../non-clickable-content-card-grid/non-clickable-content-card-grid";
import ShIcon from "../sh-icon/sh-icon";
import SingleColumnBlock from "../single-column-block/single-column-block";
import TwoColumnBlock from "../two-column-block/two-column-block";
import "./tabbed-content.scss";

export type TabsProps = {
  content: TabbedContentContainer;
  corewellStyleOverrides?: boolean;
  featureToggles: FeatureToggle[];
  iconPack: IconPack[];
  containerBackground?: "white" | "grey" | "dark-blue";
};

const TabbedContent: React.FC<TabsProps> = ({ content, corewellStyleOverrides, featureToggles, iconPack, containerBackground }) => {
  // if this isnt present the gatsby server side build fails: https://www.gatsbyjs.com/docs/debugging-html-builds/#how-to-check-if-window-is-defined
  let queryParams = new URLSearchParams();
  const isBrowser = typeof window !== "undefined";
  /* istanbul ignore else */
  if (isBrowser) {
    queryParams = new URLSearchParams(window?.location.search);
  }

  const [activeTab, setActiveTab] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs = content.tab_content_blocks || [];
  const tabLabelSize = tabs.length <= 3 ? "lg-label" : "md-label";

  const handleTabClick = (tabIndex: number) => {
    const clickedTab = tabIndex;
    setActiveTab(clickedTab);
    tabIndex !== activeTab && setMobileMenuOpen(false);
    history.pushState({}, "", `?tabContent=${tabs[clickedTab].tabbed_content.tab_unique_id}`);
  };

  // check if the url contains an anchor that correlates with a tab and set the activeTab accordingly
  useEffect(() => {
    const anchor = window.location.hash.substring(1);
    tabs.forEach((tab, index) => {
      if (anchor && (tab.tabbed_content.tab_unique_id === anchor || `tab-${index + 1}` === anchor)) {
        setActiveTab(index);
      }
    });
  }, [tabs]);

  const tabContentBlocks = (slide: TabbedContentBlock, index: number) =>
    slide.tabbed_content.tab_modular_block.map((block) => (
      <Flex className="tabbed-block-margin-y" width={1}>
        {block.accordion ? (
          <ContentAccordion
            contentAccordion={block.accordion.accordion_block[0]}
            featureToggles={featureToggles}
            iconPack={iconPack}
            insideOfTab={true}
          />
        ) : block.single_column_content_block ? (
          <SingleColumnBlock column={block.single_column_content_block} iconPack={iconPack} featureToggles={featureToggles} />
        ) : block.two_column_content_block ? (
          <TwoColumnBlock content={block.two_column_content_block} iconPack={iconPack} featureToggles={featureToggles} />
        ) : block.non_clickable_content_card_grid ? (
          <NonClickableContentCardGrid
            content={block.non_clickable_content_card_grid}
            featureToggles={featureToggles}
            iconPack={iconPack}
          />
        ) : block.high_impact_cta ? (
          <CallToAction impact="high" content={block.high_impact_cta} iconPack={iconPack} />
        ) : block.low_impact_cta ? (
          <CallToAction impact="low" content={block.low_impact_cta} iconPack={iconPack} />
        ) : block.dynamic_card_grid ? (
          <DynamicCardContainer
            content={block.dynamic_card_grid.dynamic_card_grid_reference[0]}
            featureToggles={featureToggles}
            iconPack={iconPack}
            dynamicContainerOverride={false}
          />
        ) : null}
      </Flex>
    ));

  const mobileSlideRenders = content?.tab_content_blocks?.map((slide, index) => (
    <div
      id={`tabpanel-${index}`}
      key={index}
      role="tabpanel"
      aria-labelledby={`tab-${index}`}
      className={`tab-content-container ${activeTab === index && "active"}`}
    >
      {tabContentBlocks(slide, index)}
    </div>
  ));

  const desktopSlideRenders = content?.tab_content_blocks?.map((slide, index) => (
    <div
      id={`tabpanel-${index}`}
      key={index}
      role="tabpanel"
      aria-labelledby={`tab-${index}`}
      className={`tab-content-container ${activeTab < index ? `next-inactive` : activeTab > index ? `prev-inactive` : "active"}`}
    >
      {tabContentBlocks(slide, index)}
    </div>
  ));

  const mobileTabLabels = (
    <div className="mobile-tab-labels">
      <div className={`mobile-tab-name-display ${containerBackground === "white" ? "white-bg" : "grey-bg"}`}>
        <button
          className="unstyled-btn mobile-tab-btn"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          data-testid="mobile-tab-btn-test"
        >
          <div className="mobile-tab-name" data-testid="mobile-tab-name-test">
            {tabs?.[activeTab]?.tabbed_content.tab_label}
          </div>
          <div className="mobile-tab-icon-container">
            <ShIcon
              iconPack={iconPack}
              iconName="bold_caret_down"
              className={mobileMenuOpen ? "icon bold-caret-up-icon" : "icon bold-caret-down-icon"}
            />
          </div>
        </button>
      </div>
      {mobileMenuOpen && (
        <ul role="tablist" className="mobile-tab-label-list">
          <Flex className="mobile-label-container" flexDirection={"column"}>
            {content.tab_content_blocks &&
              content.tab_content_blocks.map((_, index) => (
                <button
                  id={tabs[index].tabbed_content.tab_unique_id || `tab-${index + 1}`}
                  role="tab"
                  aria-selected={activeTab === index ? "true" : "false"}
                  key={index}
                  onClick={() => handleTabClick(index)}
                  className={`mobile-tab-list-btn unstyled-btn`}
                  data-testid={`mobile-label-btn-${index}`}
                >
                  <li role="presentation" className={`mobile-tab-label ${activeTab === index && "mobile-active-tab"}`}>
                    <div className="tab-label-text-wrapper">{content.tab_content_blocks[index].tabbed_content.tab_label}</div>
                    {activeTab === index && <ShIcon iconPack={iconPack} className="check-icon" iconName="check" />}
                  </li>
                </button>
              ))}
          </Flex>
        </ul>
      )}
    </div>
  );

  const desktopTabLabels = (
    <ul role="tablist" className="tab-label-list">
      <Flex className={`tab-label-container ${containerBackground === "white" ? "white-bg" : "grey-bg"}`}>
        {content.tab_content_blocks &&
          content.tab_content_blocks.map((_, index) => (
            <button
              id={tabs[index].tabbed_content.tab_unique_id || `tab-${index + 1}`}
              role="tab"
              aria-selected={activeTab === index ? "true" : "false"}
              key={index}
              onClick={() => handleTabClick(index)}
              className={`tab-btn unstyled-btn`}
              data-testid={`label-button-${index}`}
            >
              <li
                role="presentation"
                className={`tab-label ${tabLabelSize} ${activeTab === index ? "active-tab" : ""}`}
                data-testid={`label-test-id-${index}`}
              >
                {content.tab_content_blocks[index].tabbed_content.tab_label}
              </li>
            </button>
          ))}
      </Flex>
    </ul>
  );

  return (
    <Flex className="tabbed-content" flexDirection="column">
      <Media lessThan="desktop">{mobileTabLabels}</Media>
      <Media greaterThanOrEqual="desktop">{desktopTabLabels}</Media>
      <Flex className="tabs-container">
        <Media lessThan="desktop" className="width-100">
          {mobileSlideRenders?.[activeTab]}
        </Media>
        <Media greaterThanOrEqual="desktop" className="width-100">
          {desktopSlideRenders}
        </Media>
      </Flex>
    </Flex>
  );
};

export default TabbedContent;
